import AuthModal from 'components/modals/AuthModal';
import {
  createContext,
  FC,
  ReactNode,
  useCallback,
  useEffect,
  useState,
} from 'react';
import { $session } from 'state/stores';

interface AuthModalContextType {
  openAuthModal: (options?: AuthModalOptions) => void;
  closeAuthModal: () => void;
  isAuthModalOpen: boolean;
}

interface AuthModalOptions {
  onAuthSuccess?: () => void;
  redirectUrl?: string;
}

export const AuthModalContext = createContext<AuthModalContextType | undefined>(
  undefined,
);

interface AuthModalProviderProps {
  children: ReactNode;
}

export const AuthModalProvider: FC<AuthModalProviderProps> = ({ children }) => {
  const [isOpen, setIsOpen] = useState(false);
  const [modalOptions, setModalOptions] = useState<AuthModalOptions>({});
  const isAuth = $session.selectors.useIsAuth();
  const [wasOpen, setWasOpen] = useState(false);

  const openAuthModal = useCallback(
    (options: AuthModalOptions = {}) => {
      // Don't open if already authenticated
      if (isAuth) {
        options.onAuthSuccess?.();
        return;
      }
      setModalOptions(options);
      setIsOpen(true);
      setWasOpen(true);
    },
    [isAuth],
  );

  const closeAuthModal = useCallback(() => {
    setIsOpen(false);
    setWasOpen(false);
    // Clear options after a delay to allow for cleanup
    setTimeout(() => {
      setModalOptions({});
    }, 100);
  }, []);

  // Auto-close and trigger success when user becomes authenticated
  useEffect(() => {
    if (isAuth && wasOpen && isOpen) {
      const successCallback = modalOptions.onAuthSuccess;
      closeAuthModal();
      // Call success callback after modal is closed
      setTimeout(() => {
        successCallback?.();
      }, 150);
    }
  }, [isAuth, wasOpen, isOpen, modalOptions.onAuthSuccess, closeAuthModal]);

  const contextValue: AuthModalContextType = {
    openAuthModal,
    closeAuthModal,
    isAuthModalOpen: isOpen,
  };

  return (
    <AuthModalContext.Provider value={contextValue}>
      {children}
      <AuthModal
        isOpen={isOpen}
        onClose={closeAuthModal}
        redirectUrl={modalOptions.redirectUrl}
      />
    </AuthModalContext.Provider>
  );
};

export default AuthModalProvider;
